import type {
  MediaFile,
  MediaKind,
} from "@/components/common/media-preview/types";
import {
  formatFileSize,
  getFileExtension,
  inferMediaKind,
} from "@/lib/utils/mediaFile";

export const CARD_INTERACTIVE_CLASS =
  "cursor-pointer rounded-md border bg-gray-50 p-3 transition-colors hover:bg-gray-100";

export { formatFileSize, getFileExtension };

export type TileLayoutMode = "fill-column" | "fixed-width" | "fit-content";
export const TILE_GRID_GAP_CLASS = "gap-2 md:gap-3";
export const TILE_FIXED_WIDTH_CLASS =
  "w-[calc((100%-0.5rem)/2)] md:w-[calc((100%-0.75rem)/2)]";
export const SIGNED_MEDIA_URL_FRESHNESS_BUFFER_SECONDS = 15;

export const getMediaKind = (file: MediaFile): MediaKind => {
  const kind = inferMediaKind({
    fileName: file.file_name,
    mimeType: file.mime_type,
  });
  return kind === "spreadsheet" ? "file" : kind;
};

const LOCALHOST_NAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0", "::1"]);

const upgradeInsecureRemoteUrl = (url: string): string => {
  if (url.startsWith("/") && !url.startsWith("//")) {
    return url;
  }

  try {
    const baseUrl =
      typeof window === "undefined"
        ? "http://localhost"
        : window.location.origin;
    const parsedUrl = new URL(url, baseUrl);
    if (
      parsedUrl.protocol === "http:" &&
      !LOCALHOST_NAMES.has(parsedUrl.hostname)
    ) {
      parsedUrl.protocol = "https:";
    }
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

export const getMediaThumbnailUrl = (file: MediaFile): string =>
  upgradeInsecureRemoteUrl(file.thumbnail_url || file.url);

export const getMediaPreviewUrl = (file: MediaFile): string =>
  upgradeInsecureRemoteUrl(
    file.preview_url && hasFreshSignedMediaUrl(file)
      ? file.preview_url
      : file.url,
  );

export const getMediaDownloadUrl = (file: MediaFile): string | null =>
  file.download_url ? upgradeInsecureRemoteUrl(file.download_url) : null;

export const isAccessUrlFresh = (
  accessUrlExpiresAt?: number | null,
): boolean => {
  if (accessUrlExpiresAt == null) {
    return true;
  }

  return (
    accessUrlExpiresAt - Math.floor(Date.now() / 1000) >
    SIGNED_MEDIA_URL_FRESHNESS_BUFFER_SECONDS
  );
};

export const hasFreshSignedMediaUrl = (file: MediaFile): boolean => {
  return Boolean(
    (file.preview_url || file.download_url) &&
    isAccessUrlFresh(file.access_url_expires_at),
  );
};
